// src/utils/plan/planConfig.js
//
// Shared configuration that makes the Plan system work for both SAT and ACT.
// SAT has 2 subjects scored 200–800 (composite = sum); ACT has 4 sections
// scored 1–36 (composite = rounded average). The Plan UI derives subject
// ordering, score ranges, and composite math from here based on plan.type.

/** Plan type for admin-authored plans: any subject, no score model. */
export const CUSTOM_PLAN_TYPE = "custom";

export const PLAN_TYPES = {
  SAT: { label: "SAT", subjects: ["Reading & Writing", "Math"], min: 200, max: 800, composite: "sum" },
  ACT: { label: "ACT", subjects: ["English", "Math", "Reading", "Science"], min: 1, max: 36, composite: "avg" },
  // Custom plans have no fixed subjects (each plan declares its own) and no
  // composite score — `composite: "none"` short-circuits computeComposite.
  [CUSTOM_PLAN_TYPE]: { label: "Custom", subjects: [], min: null, max: null, composite: "none" },
};

export function planTypeOf(planOrType) {
  const t = typeof planOrType === "string" ? planOrType : planOrType?.type;
  return PLAN_TYPES[t] ? t : "SAT";
}

/** True when the plan is an admin-authored custom plan (not SAT/ACT). */
export function isCustomPlan(planOrType) {
  return planTypeOf(planOrType) === CUSTOM_PLAN_TYPE;
}

export function planConfigFor(planOrType) {
  return PLAN_TYPES[planTypeOf(planOrType)];
}

export function planSubjects(planOrType) {
  return planConfigFor(planOrType).subjects;
}

/** Composite score from a list of section scores (sum for SAT, avg for ACT). */
export function computeComposite(type, scores) {
  if (planConfigFor(type).composite === "none") return null;
  const vals = (scores || []).map(Number).filter((n) => Number.isFinite(n));
  if (!vals.length) return null;
  const sum = vals.reduce((a, b) => a + b, 0);
  return PLAN_TYPES[planTypeOf(type)].composite === "avg" ? Math.round(sum / vals.length) : sum;
}

/** Ordered subjects present in a plan's checklist, using the type's canonical order. */
export function planSubjectOrder(plan) {
  const present = Array.from(new Set((plan?.checklist || []).map((i) => i.subject)));

  // Custom plans declare their own subject order on the document.
  if (isCustomPlan(plan)) {
    const declared = Array.isArray(plan?.subjects) ? plan.subjects : [];
    const ordered = declared.filter((s) => present.includes(s));
    present.forEach((s) => {
      if (!ordered.includes(s)) ordered.push(s);
    });
    return ordered;
  }

  const canonical = planSubjects(plan);
  const ordered = canonical.filter((s) => present.includes(s));
  // include any unexpected subjects at the end (defensive)
  present.forEach((s) => {
    if (!ordered.includes(s)) ordered.push(s);
  });
  return ordered.length ? ordered : canonical;
}

/**
 * Subject score pairs [{ subject, current, target }] for display.
 * Prefers plan.subjectScores; falls back to legacy SAT fields on older plans.
 */
export function getPlanSubjectScores(plan) {
  if (Array.isArray(plan?.subjectScores) && plan.subjectScores.length) return plan.subjectScores;
  // Legacy SAT plans stored individual fields.
  const out = [];
  if (plan?.currentRwScore != null || plan?.targetRwScore != null) {
    out.push({ subject: "Reading & Writing", current: plan.currentRwScore ?? null, target: plan.targetRwScore ?? null });
  }
  if (plan?.currentMathScore != null || plan?.targetMathScore != null) {
    out.push({ subject: "Math", current: plan.currentMathScore ?? null, target: plan.targetMathScore ?? null });
  }
  return out;
}
